import React, { useMemo, useRef, useState } from 'react';
import { BiPlus, BiTrash } from 'react-icons/bi';

import { Checkbox, Input, Select } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import { HA_UNIT_CODE, KG } from 'constants/gcConstants';
import { useFetchGMoaGroups } from 'hooks/useFetchGMoaGroups';
import { ADD_RECORD_SAVE_FAILED } from 'constants/messageConstants';
import {
  ADD_RECORD_ADDING_LABEL,
  ADD_RECORD_BUTTON_LABEL,
  ADD_RECORD_CONSTITUENT_LABELS,
  ADD_RECORD_FIELD_LABELS,
  ADD_RECORD_NUTRIENT_LABELS,
  ADD_RECORD_POISON_NONE_LABEL,
  ADD_RECORD_POISON_SCHEDULE_LABEL,
  ADD_RECORD_SELECT_UNIT_PLACEHOLDER,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import type { PoisonSchedule } from 'interfaces/IInputStep';
import { v4 as uuidv4 } from 'uuid';
import MergeClassNames from 'utils/MergeClassNames';

import {
  ActiveConstituentDraft,
  ADD_PRODUCT_CONFIG,
  AddProductKind,
  AddRecordValues,
  isAddRecordValid,
  NUTRIENT_KEYS,
  NutrientKey,
  REMOVAL_KEYS,
  RemovalKey,
} from './addRecord';
import { FLYOUT_ADD_BUTTON_ATTRIBUTE, StepSelectorOption } from './stepConfig';
import { getRateUnitOptions } from './stepCalculations';
import { useCreateMRecord } from './useCreateMRecord';

interface AddRecordFormProps {
  kind: AddProductKind;
  /** The text typed in the picker search, pre-filled as the name. */
  initialName: string;
  gUnits: GUnit[];
  /** Called with the saved record, ready to be selected, and the values it was saved with. */
  onCreated: (option: StepSelectorOption, values: AddRecordValues) => void;
}

const POISON_SCHEDULES: PoisonSchedule[] = [0, 1, 2, 3, 4, 5];

const toIUnit = (unit: GUnit | undefined): IUnit | null =>
  unit ? { unit: unit.code, globalId: unit.globalId } : null;

/** A label above its control, so every field in the form reads the same way. */
const Field: React.FC<{
  label: string;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, className, children }) => (
  <div className={MergeClassNames('flex min-w-0 flex-col gap-1', className)}>
    <label htmlFor={htmlFor} className="text-sm font-semibold text-gray-dark">
      {label}
    </label>
    {children}
  </div>
);

/**
 * Add form for a missing product (OP-2498), shown in a flyout beside the step
 * picker or inline under a plan field. Saves to the user's own list and hands
 * the new record back to be selected. A failed save keeps the values and says
 * so inline.
 */
const AddRecordForm: React.FC<AddRecordFormProps> = ({
  kind,
  initialName,
  gUnits,
  onCreated,
}) => {
  const config = ADD_PRODUCT_CONFIG[kind];
  // One id per open form, reused on retry so a resend can't create two records.
  const idRef = useRef(uuidv4());
  const { mutateAsync, isPending, isError } = useCreateMRecord(kind);

  const [values, setValues] = useState<AddRecordValues>({
    name: initialName.trim(),
  });
  const patch = (next: Partial<AddRecordValues>) =>
    setValues((current) => ({ ...current, ...next }));

  const constituents = values.activeConstituents ?? [];
  const addConstituent = () =>
    patch({
      activeConstituents: [...constituents, { key: uuidv4(), name: '' }],
    });
  const patchConstituent = (
    key: string,
    next: Partial<ActiveConstituentDraft>
  ) =>
    patch({
      activeConstituents: constituents.map((draft) =>
        draft.key === key ? { ...draft, ...next } : draft
      ),
    });
  const removeConstituent = (key: string) =>
    patch({
      activeConstituents: constituents.filter((draft) => draft.key !== key),
    });

  const unitOptions = useMemo(
    () => getRateUnitOptions(config.stepType, gUnits),
    [config.stepType, gUnits]
  );
  // Operations are always $/ha and crops always kg, so there is no unit to choose.
  const fixedUnit = useMemo(() => {
    if (config.hasUnitChoice) return null;
    const code = kind === 'operation' ? HA_UNIT_CODE : KG;
    return toIUnit(gUnits.find((unit) => unit.code === code));
  }, [config.hasUnitChoice, gUnits, kind]);

  const isPlantProtection = kind === 'chemical' || kind === 'adjuvant';
  const { data: moaGroups = [] } = useFetchGMoaGroups(isPlantProtection);
  const moaOptions = useMemo(
    () => moaGroups.map((group) => ({ value: group.id, label: group.name })),
    [moaGroups]
  );
  const selectUnit = (globalId: string): IUnit | null =>
    toIUnit(gUnits.find((unit) => unit.globalId === globalId));

  const effectiveValues: AddRecordValues = {
    ...values,
    unit: fixedUnit ?? values.unit,
  };
  const canSave = isAddRecordValid(kind, effectiveValues) && !isPending;

  const handleSave = () => {
    if (!canSave) return;
    // Not mutate()'s onSuccess: the lists refetch before the save resolves, and
    // if this form's host re-renders without it a callback tied to the mounted
    // component would never run, leaving the new record created but unselected.
    mutateAsync({ id: idRef.current, values: effectiveValues }).then(
      (option) => onCreated(option, effectiveValues),
      () => undefined // the failure shows inline via isError
    );
  };

  const costLabel =
    kind === 'operation'
      ? ADD_RECORD_FIELD_LABELS.operationCost
      : kind === 'crop'
        ? ADD_RECORD_FIELD_LABELS.cropCost
        : ADD_RECORD_FIELD_LABELS.price;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-3 overflow-y-auto p-3">
        <Field
          label={ADD_RECORD_FIELD_LABELS.name}
          htmlFor="add-record-name"
          className="col-span-2"
        >
          <Input
            id="add-record-name"
            autoFocus
            value={values.name}
            onChange={(event) => patch({ name: event.target.value })}
            onPressEnter={handleSave}
          />
        </Field>

        {config.hasRate && (
          <Field
            label={ADD_RECORD_FIELD_LABELS.rate}
            htmlFor="add-record-rate"
          >
            <NumericInputField
              id="add-record-rate"
              size="middle"
              className="w-full"
              inputClassName="w-full"
              min={0}
              inclusiveMin
              value={values.rate ?? null}
              onChange={(rate) => patch({ rate })}
            />
          </Field>
        )}

        {config.hasUnitChoice && (
          <Field
            label={ADD_RECORD_FIELD_LABELS.rateUnit}
            htmlFor="add-record-unit"
          >
            <Select
              id="add-record-unit"
              className="w-full"
              showSearch
              optionFilterProp="label"
              placeholder={ADD_RECORD_SELECT_UNIT_PLACEHOLDER}
              options={unitOptions}
              value={values.unit?.globalId ?? undefined}
              getPopupContainer={() => document.body}
              onChange={(globalId: string) =>
                patch({ unit: selectUnit(globalId) })
              }
            />
          </Field>
        )}

        <Field label={costLabel} htmlFor="add-record-cost">
          <NumericInputField
            id="add-record-cost"
            size="middle"
            className="w-full"
            inputClassName="w-full"
            min={0}
            inclusiveMin
            value={values.cost ?? null}
            onChange={(cost) => patch({ cost })}
          />
        </Field>

        {isPlantProtection && (
          <>
            <Field
              label={ADD_RECORD_FIELD_LABELS.formulationCode}
              htmlFor="add-record-formulation"
            >
              <Input
                id="add-record-formulation"
                value={values.formulationCode ?? ''}
                onChange={(event) =>
                  patch({ formulationCode: event.target.value })
                }
              />
            </Field>
            <Field
              label={ADD_RECORD_FIELD_LABELS.manufacturer}
              htmlFor="add-record-manufacturer"
            >
              <Input
                id="add-record-manufacturer"
                value={values.snapManufacturer ?? ''}
                onChange={(event) =>
                  patch({ snapManufacturer: event.target.value })
                }
              />
            </Field>
            <Field
              label={ADD_RECORD_FIELD_LABELS.apvmaNumber}
              htmlFor="add-record-apvma"
            >
              <Input
                id="add-record-apvma"
                value={values.apvmaNumber ?? ''}
                onChange={(event) => patch({ apvmaNumber: event.target.value })}
              />
            </Field>
            <Field
              label={ADD_RECORD_FIELD_LABELS.poisonSchedule}
              htmlFor="add-record-poison"
            >
              <Select
                id="add-record-poison"
                className="w-full"
                value={values.poisonSchedule ?? undefined}
                getPopupContainer={() => document.body}
                options={POISON_SCHEDULES.map((schedule) => ({
                  value: schedule,
                  label:
                    schedule === 0
                      ? ADD_RECORD_POISON_NONE_LABEL
                      : ADD_RECORD_POISON_SCHEDULE_LABEL(schedule),
                }))}
                onChange={(poisonSchedule: PoisonSchedule) =>
                  patch({ poisonSchedule })
                }
              />
            </Field>

            <section className="col-span-2 flex flex-col gap-3 border-t border-gray-bright pt-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-charcoal">
                  {ADD_RECORD_CONSTITUENT_LABELS.section}
                </h3>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-[var(--radius-button)] px-2 py-1 text-sm font-medium text-charcoal hover:bg-gray-smoke"
                  onClick={addConstituent}
                >
                  <BiPlus aria-hidden />
                  {ADD_RECORD_CONSTITUENT_LABELS.add}
                </button>
              </div>

              <Field
                label={ADD_RECORD_CONSTITUENT_LABELS.activeUnit}
                htmlFor="add-record-active-unit"
              >
                <Select
                  id="add-record-active-unit"
                  className="w-full"
                  showSearch
                  optionFilterProp="label"
                  placeholder={ADD_RECORD_SELECT_UNIT_PLACEHOLDER}
                  options={unitOptions}
                  value={values.activeUnit?.globalId ?? undefined}
                  getPopupContainer={() => document.body}
                  onChange={(globalId: string) =>
                    patch({ activeUnit: selectUnit(globalId) })
                  }
                />
              </Field>

              {constituents.map((draft, index) => (
                <fieldset
                  key={draft.key}
                  className="grid grid-cols-2 gap-3 rounded-md border border-gray-bright p-3"
                >
                  <Field
                    label={ADD_RECORD_CONSTITUENT_LABELS.name}
                    htmlFor={`add-record-constituent-name-${index}`}
                    className="col-span-2"
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        id={`add-record-constituent-name-${index}`}
                        value={draft.name}
                        onChange={(event) =>
                          patchConstituent(draft.key, {
                            name: event.target.value,
                          })
                        }
                      />
                      <button
                        type="button"
                        aria-label={ADD_RECORD_CONSTITUENT_LABELS.remove}
                        className="flex flex-shrink-0 items-center justify-center rounded-[var(--radius-button)] p-1.5 text-gray-dark hover:bg-gray-smoke"
                        onClick={() => removeConstituent(draft.key)}
                      >
                        <BiTrash aria-hidden />
                      </button>
                    </div>
                  </Field>
                  <Field
                    label={ADD_RECORD_CONSTITUENT_LABELS.concentration}
                    htmlFor={`add-record-constituent-conc-${index}`}
                  >
                    <NumericInputField
                      id={`add-record-constituent-conc-${index}`}
                      size="middle"
                      className="w-full"
                      inputClassName="w-full"
                      min={0}
                      inclusiveMin
                      value={draft.concentration ?? null}
                      onChange={(concentration) =>
                        patchConstituent(draft.key, { concentration })
                      }
                    />
                  </Field>
                  <Field
                    label={ADD_RECORD_CONSTITUENT_LABELS.concentrationUnit}
                    htmlFor={`add-record-constituent-unit-${index}`}
                  >
                    <Select
                      id={`add-record-constituent-unit-${index}`}
                      className="w-full"
                      showSearch
                      optionFilterProp="label"
                      placeholder={ADD_RECORD_SELECT_UNIT_PLACEHOLDER}
                      options={unitOptions}
                      value={draft.concentrationUnit?.globalId ?? undefined}
                      getPopupContainer={() => document.body}
                      onChange={(globalId: string) =>
                        patchConstituent(draft.key, {
                          concentrationUnit: selectUnit(globalId),
                        })
                      }
                    />
                  </Field>
                  <Field
                    label={ADD_RECORD_CONSTITUENT_LABELS.moaGroup}
                    htmlFor={`add-record-constituent-moa-${index}`}
                    className="col-span-2"
                  >
                    <Select
                      id={`add-record-constituent-moa-${index}`}
                      className="w-full"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      placeholder={ADD_RECORD_CONSTITUENT_LABELS.selectMoaGroup}
                      options={moaOptions}
                      value={draft.moaGroup?.id}
                      getPopupContainer={() => document.body}
                      onChange={(id?: string) => {
                        const group = moaGroups.find((g) => g.id === id);
                        patchConstituent(draft.key, {
                          moaGroup: group
                            ? { id: group.id, name: group.name }
                            : null,
                        });
                      }}
                    />
                  </Field>
                </fieldset>
              ))}
            </section>
          </>
        )}

        {kind === 'crop' && (
          <details className="col-span-2">
            <summary className="cursor-pointer text-sm font-semibold text-gray-dark">
              {ADD_RECORD_FIELD_LABELS.removal}
            </summary>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {REMOVAL_KEYS.map((key: RemovalKey) => (
                <Field
                  key={key}
                  label={
                    ADD_RECORD_NUTRIENT_LABELS[
                      key.replace(
                        'product',
                        'nutrient'
                      ) as keyof typeof ADD_RECORD_NUTRIENT_LABELS
                    ]
                  }
                  htmlFor={`add-record-${key}`}
                >
                  <NumericInputField
                    id={`add-record-${key}`}
                    size="middle"
                    className="w-full"
                    inputClassName="w-full"
                    min={0}
                    inclusiveMin
                    value={values.removal?.[key] ?? null}
                    onChange={(value) =>
                      patch({ removal: { ...values.removal, [key]: value } })
                    }
                  />
                </Field>
              ))}
            </div>
          </details>
        )}

        {kind === 'soilInput' && (
          <>
            <Checkbox
              className="col-span-2"
              checked={values.isLiquid ?? false}
              onChange={(event) => patch({ isLiquid: event.target.checked })}
            >
              {ADD_RECORD_FIELD_LABELS.liquid}
            </Checkbox>
            <details className="col-span-2">
              <summary className="cursor-pointer text-sm font-semibold text-gray-dark">
                {ADD_RECORD_FIELD_LABELS.nutrients}
              </summary>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {NUTRIENT_KEYS.map((key: NutrientKey) => (
                  <Field
                    key={key}
                    label={ADD_RECORD_NUTRIENT_LABELS[key]}
                    htmlFor={`add-record-${key}`}
                  >
                    <NumericInputField
                      id={`add-record-${key}`}
                      size="middle"
                      className="w-full"
                      inputClassName="w-full"
                      min={0}
                      inclusiveMin
                      value={values.nutrients?.[key] ?? null}
                      onChange={(value) =>
                        patch({
                          nutrients: { ...values.nutrients, [key]: value },
                        })
                      }
                    />
                  </Field>
                ))}
              </div>
            </details>
          </>
        )}
      </div>
      <div className="flex flex-col gap-2 p-3">
        {isError && (
          <p role="alert" className="text-xs font-semibold text-danger-dark">
            {ADD_RECORD_SAVE_FAILED}
          </p>
        )}
        <button
          type="button"
          {...{ [FLYOUT_ADD_BUTTON_ATTRIBUTE]: '' }}
          disabled={!canSave}
          className={MergeClassNames(
            'w-full rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-medium text-white',
            !canSave && 'cursor-not-allowed opacity-50'
          )}
          onClick={handleSave}
        >
          {isPending ? ADD_RECORD_ADDING_LABEL : ADD_RECORD_BUTTON_LABEL}
        </button>
      </div>
    </div>
  );
};

export default AddRecordForm;
